import { LinkIcon, UploadIcon } from '@heroicons/react/outline';
import { useRouter } from 'next/router';
import React from 'react';

import clsxm from '@/lib/clsxm';
import { saveTranscriptionHandoff } from '@/lib/transcriptionHandoff';
import { detectTranscriptionUrl } from '@/lib/transcriptionUrl';

import { ArrowGlyph, tone } from '@/components/agents/primitives';
import { SourceIcon, type SourceKind } from '@/components/audio/audioUi';
import Spinner from '@/components/Spinner';

import { TRANSCRIPTION_FILE_ACCEPT } from '@/constants/transcription';
import { useAnalytics } from '@/contexts/Analytics';
import { useUser } from '@/utils/useUser';

import { useLandingT, useStartHref } from './kit';

/**
 * The landing page's upload box. Picking a file or pasting a link saves it in
 * this browser and opens the app (signing up first when needed), where the
 * transcription is offered with that file or link already filled in.
 */
export default function LandingDropzone({
  page,
  link: linkHint,
  className,
}: {
  /** Which landing page this is, for analytics. */
  page: string;
  /**
   * Pages about a link source (YouTube, TikTok...) lead with the link field;
   * the file drop becomes a small secondary option.
   */
  link?: { label: string; placeholder: string; source: SourceKind };
  className?: string;
}) {
  const { t } = useLandingT();
  const router = useRouter();
  const analytics = useAnalytics();
  const { user } = useUser();
  const startHref = useStartHref();
  const fileInput = React.useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [link, setLink] = React.useState('');
  const [linkError, setLinkError] = React.useState('');

  const continueToApp = async (value: { file: File } | { link: string }) => {
    setBusy(true);
    const saved = await saveTranscriptionHandoff(value);
    analytics.track('Transcription Landing Start', {
      page,
      kind: 'file' in value ? 'file' : 'link',
      signed_in: Boolean(user),
      saved,
    });
    await router.push(startHref);
  };

  const pickFile = (file: File | undefined) => {
    if (!file || busy) return;
    void continueToApp({ file });
  };

  const submitLink = (event: React.FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const result = detectTranscriptionUrl(link);
    if (!result.ok) {
      setLinkError(
        t(
          'Paste a public link to a YouTube, Instagram or TikTok video, a Google Drive file, or an audio or video file.',
        ),
      );
      return;
    }
    void continueToApp({ link: result.url });
  };

  const fileInputEl = (
    <input
      ref={fileInput}
      type='file'
      accept={TRANSCRIPTION_FILE_ACCEPT}
      className='hidden'
      onChange={(event) => {
        pickFile(event.target.files?.[0]);
        event.target.value = '';
      }}
    />
  );

  const dropHandlers = {
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault();
      setDragging(true);
    },
    onDragLeave: () => setDragging(false),
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      setDragging(false);
      pickFile(event.dataTransfer.files?.[0]);
    },
  };

  const linkErrorEl = linkError ? (
    <p className='px-4 pb-1 pt-2 text-xs text-rose-600 dark:text-rose-400'>
      {linkError}
    </p>
  ) : null;

  const linkInput = (big: boolean) => (
    <input
      id={`landing-link-${page}`}
      type='url'
      inputMode='url'
      value={link}
      onChange={(event) => {
        setLink(event.target.value);
        setLinkError('');
      }}
      placeholder={
        linkHint
          ? linkHint.placeholder
          : t('Or paste a YouTube, Instagram, TikTok or Drive link')
      }
      className={clsxm(
        'min-w-0 flex-1 border-0 bg-transparent px-0 focus:ring-0 placeholder:text-[#8a8aa1] dark:placeholder:text-[#6e6e8c]',
        big ? 'py-3 text-base' : 'py-2 text-sm',
        tone.ink,
      )}
    />
  );

  const submitButton = (big: boolean) => (
    <button
      type='submit'
      disabled={busy || !link.trim()}
      className={clsxm(
        'inline-flex flex-none items-center justify-center gap-1.5 rounded-full bg-primary-600 font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50',
        big ? 'px-5 py-3 text-[15px]' : 'px-4 py-2 text-sm',
      )}
    >
      {busy ? <Spinner className='h-4 w-4 text-white' /> : null}
      {t('Transcribe')}
      {busy ? null : <ArrowGlyph className='h-3.5 w-3.5' />}
    </button>
  );

  if (linkHint) {
    return (
      <div className={className}>
        <div className='rounded-[26px] bg-white p-2 shadow-[0_24px_60px_-30px_rgba(22,12,90,0.45)] ring-1 ring-[#0f0f28]/[0.07] dark:bg-[#14141f] dark:ring-white/[0.08]'>
          {/* noValidate: links without https:// are fine (detectTranscriptionUrl adds it). */}
          <form
            noValidate
            onSubmit={submitLink}
            className='rounded-[20px] bg-[#fafafd] p-4 sm:p-5 dark:bg-white/[0.03]'
          >
            <label
              htmlFor={`landing-link-${page}`}
              className={clsxm(
                'flex items-center gap-2 text-[15px] font-semibold',
                tone.ink,
              )}
            >
              <SourceIcon
                kind={linkHint.source}
                tile={false}
                className='h-5 w-5'
              />
              {t(linkHint.label)}
            </label>
            <div className='mt-3 flex flex-col gap-2 sm:flex-row sm:items-center'>
              <div className='flex min-w-0 flex-1 items-center gap-2 rounded-full bg-white px-4 ring-1 ring-[#0f0f28]/10 focus-within:ring-2 focus-within:ring-primary-500 dark:bg-white/[0.06] dark:ring-white/15'>
                <LinkIcon
                  className={clsxm('h-4 w-4 flex-none', tone.quiet)}
                  aria-hidden
                />
                {linkInput(true)}
              </div>
              {submitButton(true)}
            </div>
          </form>
          {linkErrorEl}
          <button
            type='button'
            disabled={busy}
            onClick={() => fileInput.current?.click()}
            {...dropHandlers}
            className={clsxm(
              'mt-2 flex w-full items-center justify-center gap-2 rounded-[20px] border-2 border-dashed px-4 py-3.5 text-sm transition disabled:cursor-wait',
              dragging
                ? 'border-primary-500 bg-primary-50/70 dark:border-primary-400 dark:bg-primary-500/10'
                : 'border-[#e2e2ec] hover:border-primary-400 hover:bg-primary-50/40 dark:border-white/10 dark:hover:border-primary-400/60 dark:hover:bg-primary-500/[0.06]',
              tone.body,
            )}
          >
            <UploadIcon className='h-4 w-4 flex-none' aria-hidden />
            {t('Or upload a video file instead')}
          </button>
          {fileInputEl}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className='rounded-[26px] bg-white p-2 shadow-[0_24px_60px_-30px_rgba(22,12,90,0.45)] ring-1 ring-[#0f0f28]/[0.07] dark:bg-[#14141f] dark:ring-white/[0.08]'>
        <button
          type='button'
          disabled={busy}
          onClick={() => fileInput.current?.click()}
          {...dropHandlers}
          className={clsxm(
            'group flex w-full flex-col items-center justify-center rounded-[20px] border-2 border-dashed px-6 py-9 text-center transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-wait',
            dragging
              ? 'border-primary-500 bg-primary-50/70 dark:border-primary-400 dark:bg-primary-500/10'
              : 'border-[#d9d9e6] bg-[#fafafd] hover:border-primary-400 hover:bg-primary-50/40 dark:border-white/15 dark:bg-white/[0.03] dark:hover:border-primary-400/60 dark:hover:bg-primary-500/[0.06]',
          )}
        >
          <span className='flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-600 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.22),0_10px_24px_-10px_rgba(79,70,229,0.8)] transition-transform duration-200 group-hover:-translate-y-0.5'>
            {busy ? (
              <Spinner className='h-5 w-5 text-white' />
            ) : (
              <UploadIcon className='h-6 w-6' aria-hidden />
            )}
          </span>
          <span className={clsxm('mt-4 text-base font-semibold', tone.ink)}>
            {busy ? t('Opening…') : t('Drop an audio or video file here')}
          </span>
          <span className={clsxm('mt-1 text-sm', tone.body)}>
            {t('or')}{' '}
            <span className='font-semibold text-primary-600 underline decoration-primary-600/30 underline-offset-4 dark:text-primary-400'>
              {t('choose a file')}
            </span>
          </span>
          <span className={clsxm('mt-3 text-xs', tone.quiet)}>
            {t('MP3, WAV, M4A, MP4, MOV and 30+ more formats')}
          </span>
        </button>
        {fileInputEl}

        {/* noValidate: links without https:// are fine (detectTranscriptionUrl adds it). */}
        <form
          noValidate
          onSubmit={submitLink}
          className='mt-2 flex items-center gap-2 rounded-[20px] bg-[#f4f4f8] py-1.5 pe-1.5 ps-4 dark:bg-white/[0.05]'
        >
          <LinkIcon
            className={clsxm('h-4 w-4 flex-none', tone.quiet)}
            aria-hidden
          />
          <label htmlFor={`landing-link-${page}`} className='sr-only'>
            {t('Paste a link')}
          </label>
          {linkInput(false)}
          {submitButton(false)}
        </form>
        {linkErrorEl}
      </div>
    </div>
  );
}
